import { useState, useEffect } from 'react';
import { FaWhatsapp, FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin, FaInstagram, FaHeart } from 'react-icons/fa';
import { getFarmers, getCustomFoodItems, getFatWisePrices, getMilkEntries, getCattleFoodEntries, getPublicFarmers, getPublicFatWisePrices, createSampleDataForLandingPage, testLandingPageData } from '../services/firebaseService';
import '../style.css';

function LandingPage({ onShowLogin }) {
  const [currentStat, setCurrentStat] = useState(0);
  const [registeredFarmers, setRegisteredFarmers] = useState([]);
  const [loadingFarmers, setLoadingFarmers] = useState(true);
  const [entries, setEntries] = useState([]);
  const [cattleFoodEntries, setCattleFoodEntries] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [loadingFood, setLoadingFood] = useState(true);
  const [cowPrices, setCowPrices] = useState([]);
  const [buffaloPrices, setBuffaloPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(true);

  const DEFAULT_CATTLE_FOOD_ITEMS = [
    { id: "grass", name: "Grass", unit: "kg", price: 50 },
    { id: "hay", name: "Hay", unit: "kg", price: 80 },
    { id: "silage", name: "Silage", unit: "kg", price: 120 },
    { id: "bran", name: "Bran", unit: "kg", price: 100 }
  ];

  // Get milk prices for stats
  const cowPrice = null;
  const buffaloPrice = null;

  const stats = [
    { number: '1,250L', label: 'Daily Milk Production', icon: '🥛' },
    { number: '45+', label: 'Active Farmers', icon: '👨‍🌾' },
    { number: '₹34/L', label: 'Cow Milk Price', icon: '🐄' },
    { number: '₹62/L', label: 'Buffalo Milk Price', icon: '🦬' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stats.length]);

  // Expose test functions globally for debugging
  useEffect(() => {
    window.testLandingPageData = testLandingPageData;
    window.createSampleDataForLandingPage = createSampleDataForLandingPage;
    console.log('🔧 Debug functions available:');
    console.log('- window.testLandingPageData() - Test if data exists');
    console.log('- window.createSampleDataForLandingPage() - Create sample data');
  }, []);

  useEffect(() => {
    const fetchFarmers = async () => {
      try {
        console.log('🏠 LandingPage: Starting to fetch farmers...');
        setLoadingFarmers(true);
        const farmers = await getPublicFarmers(false); // Use public function
        console.log('🏠 LandingPage: Received farmers:', farmers);
        setRegisteredFarmers(farmers); // Show all farmers for slider
      } catch (error) {
        console.error('🏠 LandingPage: Error fetching farmers:', error);
        setRegisteredFarmers([]);
      } finally {
        setLoadingFarmers(false);
      }
    };

    fetchFarmers();
  }, []);

  useEffect(() => {
    const fetchMilkAndFoodData = async () => {
      try {
        const milkData = await getMilkEntries(false);
        const foodData = await getCattleFoodEntries(false);
        setEntries(milkData);
        setCattleFoodEntries(foodData);
      } catch (error) {
        console.error('Error fetching milk and food data:', error);
        setEntries([]);
        setCattleFoodEntries([]);
      }
    };

    fetchMilkAndFoodData();
  }, []);

  useEffect(() => {
    const fetchFoodItems = async () => {
      try {
        setLoadingFood(true);
        const customItems = await getCustomFoodItems(false);
        setFoodItems([...DEFAULT_CATTLE_FOOD_ITEMS, ...customItems.slice(0, 4)]);
      } catch (error) {
        console.error('Error fetching food items:', error);
        setFoodItems(DEFAULT_CATTLE_FOOD_ITEMS);
      } finally {
        setLoadingFood(false);
      }
    };

    fetchFoodItems();
  }, []);

  useEffect(() => {
    const fetchFatWisePrices = async () => {
      try {
        console.log('🏠 LandingPage: Starting to fetch prices...');
        setLoadingPrices(true);
        const cowData = await getPublicFatWisePrices('cow', false); // Use public function
        const buffaloData = await getPublicFatWisePrices('buffalo', false); // Use public function
        console.log('🏠 LandingPage: Received cow prices:', cowData);
        console.log('🏠 LandingPage: Received buffalo prices:', buffaloData);
        setCowPrices(cowData.slice(0, 10)); // Show first 10 entries
        setBuffaloPrices(buffaloData.slice(0, 10));
      } catch (error) {
        console.error('🏠 LandingPage: Error fetching prices:', error);
        setCowPrices([]);
        setBuffaloPrices([]);
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchFatWisePrices();
  }, []);

  const handleLoginClick = () => {
    onShowLogin();
  };

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg-pattern"></div>
        
        <div className="hero-wrapper">
          {/* Left Content */}
          <div className="hero-left">
            <div className="hero-badge-container">
              <span className="hero-badge">
                <span className="badge-icon">🏆</span>
                #1 Dairy Management System in India
              </span>
            </div>

            <div className="hero-icon-wrapper">
              <span className="hero-cow-icon">🐄</span>
              <div className="hero-icon-ring"></div>
            </div>

            <h1 className="hero-heading">
              <span className="heading-small">Transform Your</span>
              <span className="heading-large">Dairy Business</span>
              <span className="heading-sub">with Smart Technology</span>
            </h1>

            <p className="hero-desc">
              The complete dairy management solution trusted by 500+ farms across India
            </p>

            <ul className="hero-features-list">
              <li><span className="feature-bullet">⚡</span> Real-time milk tracking</li>
              <li><span className="feature-bullet">📱</span> Mobile-first design</li>
              <li><span className="feature-bullet">🔒</span> Bank-grade security</li>
            </ul>

            <div className="hero-buttons">
              <button className="btn-primary-hero" onClick={handleLoginClick}>
                <span className="btn-icon">🔐</span>
                <span className="btn-label">
                  <span className="btn-main-text">Login</span>
                  <span className="btn-sub-text">Access your account</span>
                </span>
              </button>
              <button className="btn-secondary-hero" onClick={scrollToFeatures}>
                <span className="btn-icon">▶️</span>
                <span>View Features</span>
              </button>
            </div>
          </div>

          {/* Right Content - Stats & Preview */}
          <div className="hero-right">
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className={`stat-box ${index === currentStat ? 'active' : ''}`}>
                  <div className="stat-icon-box">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="stat-info">
                    <span className="stat-num">{stat.number}</span>
                    <span className="stat-lbl">{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="dashboard-preview-new">
              <div className="preview-window">
                <div className="window-bar">
                  <div className="window-dots">
                    <span className="w-dot red"></span>
                    <span className="w-dot yellow"></span>
                    <span className="w-dot green"></span>
                  </div>
                  <span className="window-title">🐄 Akshay's Dairy Management</span>
                </div>
                <div className="window-body">
                  <div className="window-sidebar">
                    <div className="sidebar-item active">🥛 Milk Entry</div>
                    <div className="sidebar-item">👨‍🌾 Farmers</div>
                    <div className="sidebar-item">📊 Reports</div>
                    <div className="sidebar-item">💳 Billing</div>
                  </div>
                  <div className="window-main">
                    <div className="main-header">
                      <h4>Today's Collection</h4>
                      <span className="date-badge">📅 Dec 2024</span>
                    </div>
                    <div className="main-stats">
                      <div className="main-stat-card">
                        <span className="stat-emoji">�</span>
                        <div className="stat-data">
                          <span className="stat-value">1,250L</span>
                          <span className="stat-name">Total Milk</span>
                        </div>
                      </div>
                      <div className="main-stat-card">
                        <span className="stat-emoji">👨‍🌾</span>
                        <div className="stat-data">
                          <span className="stat-value">45</span>
                          <span className="stat-name">Farmers</span>
                        </div>
                      </div>
                      <div className="main-stat-card highlight">
                        <span className="stat-emoji">💰</span>
                        <div className="stat-data">
                          <span className="stat-value">₹85K</span>
                          <span className="stat-name">Revenue</span>
                        </div>
                      </div>
                    </div>
                    <div className="main-chart">
                      <div className="chart-label">Weekly Trend</div>
                      <div className="chart-visual">
                        <div className="chart-bar" style={{ height: '50%' }}><span>M</span></div>
                        <div className="chart-bar" style={{ height: '70%' }}><span>T</span></div>
                        <div className="chart-bar" style={{ height: '60%' }}><span>W</span></div>
                        <div className="chart-bar" style={{ height: '85%' }}><span>T</span></div>
                        <div className="chart-bar" style={{ height: '75%' }}><span>F</span></div>
                        <div className="chart-bar" style={{ height: '90%' }}><span>S</span></div>
                        <div className="chart-bar active" style={{ height: '100%' }}><span>S</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="section-container">
          <div className="section-head">
            <span className="section-badge">✨ Features</span>
            <h2 className="section-title">Everything you need to manage your dairy</h2>
            <p className="section-subtitle">Powerful tools designed specifically for modern dairy operations</p>
          </div>

          <div className="features-grid">
            <div className="feature-card featured">
              <div className="feature-icon-box">🥛</div>
              <span className="feature-badge">Most Popular</span>
              <h3>Smart Milk Tracking</h3>
              <p>AI-powered milk production tracking with quality analysis and automated reporting.</p>
              <ul className="feature-list">
                <li>✓ Real-time tracking</li>
                <li>✓ Quality analysis</li>
                <li>✓ Auto reports</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-icon-box">👨‍🌾</div>
              <h3>Farmer Management</h3>
              <p>Complete farmer profiles with payment history and performance analytics.</p>
              <ul className="feature-list">
                <li>✓ Digital profiles</li>
                <li>✓ Payment tracking</li>
                <li>✓ SMS alerts</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-icon-box">🌾</div>
              <h3>Feed Management</h3>
              <p>Track cattle feed inventory, costs, and nutrition planning.</p>
              <ul className="feature-list">
                <li>✓ Inventory tracking</li>
                <li>✓ Cost analysis</li>
                <li>✓ Nutrition plans</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Registered Farmers Section - Premium Design */}
      <section className="registered-farmers-simple">
        <div className="section-container">
          <h2 className="farmers-title">💰 Current Milk Prices (Fat-wise)</h2>

          {loadingPrices ? (
            <div className="farmers-loading-simple">
              <div className="spinner"></div>
              <p>Loading prices...</p>
            </div>
          ) : (cowPrices.length > 0 || buffaloPrices.length > 0) ? (
            <div className="price-display-grid">
              {cowPrices.length > 0 && (
                <div className="price-display-section">
                  <h3>🐄 Cow Milk Prices</h3>
                  <div className="price-display-items">
                    {cowPrices.map((price) => (
                      <div key={price.id} className="price-display-item">
                        <span className="price-fat-label">Fat {price.fat.toFixed(1)}</span>
                        <span className="price-amount-label">₹{price.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {buffaloPrices.length > 0 && (
                <div className="price-display-section">
                  <h3>🐃 Buffalo Milk Prices</h3>
                  <div className="price-display-items">
                    {buffaloPrices.map((price) => (
                      <div key={price.id} className="price-display-item">
                        <span className="price-fat-label">Fat {price.fat.toFixed(1)}</span>
                        <span className="price-amount-label">₹{price.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="farmers-empty-simple">
              <p>No milk prices set yet</p>
              <button className="btn-join" onClick={handleLoginClick}>Check Later</button>
            </div>
          )}
        </div>
      </section>

      {/* Registered Farmers Section - Premium Design */}
      <section className="farmers-section-premium">
        <div className="farmers-bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        
        <div className="section-container">
          <div className="farmers-header-premium">
            <div className="farmers-badge-wrapper">
              <span className="farmers-badge-premium">
                <span className="badge-dot"></span>
                Our Community
              </span>
            </div>
            <h2 className="farmers-title-premium">Our Registered Farmers</h2>
          </div>

          {loadingFarmers ? (
            <div className="farmers-loading-premium">
              <div className="loader-container">
                <div className="loader-ring"></div>
                <span className="loader-emoji">👨‍🌾</span>
              </div>
              <p>Loading farmers...</p>
            </div>
          ) : registeredFarmers.length > 0 ? (
            <>
              <div className="marquee-container">
                <div className="marquee-track">
                  {[...registeredFarmers, ...registeredFarmers].map((farmer, index) => {
                    const avatarColors = [
                      { gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', light: '#dbeafe' },
                      { gradient: 'linear-gradient(135deg, #10b981, #059669)', light: '#d1fae5' },
                      { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', light: '#fef3c7' },
                      { gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', light: '#ede9fe' },
                      { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', light: '#fee2e2' },
                      { gradient: 'linear-gradient(135deg, #ec4899, #db2777)', light: '#fce7f3' }
                    ];
                    const colorData = avatarColors[index % avatarColors.length];
                    
                    // Calculate total milk for this farmer
                    const farmerMilkEntries = entries.filter(e => e.farmerCode === farmer.code);
                    const totalMilk = farmerMilkEntries.reduce((sum, e) => sum + (e.quantity || 0), 0);
                    const totalMilkAmount = farmerMilkEntries.reduce((sum, e) => sum + (e.quantity * e.price || 0), 0);
                    
                    // Calculate total cattle food for this farmer
                    const farmerCattleFood = cattleFoodEntries.filter(e => e.farmerCode === farmer.code);
                    const totalCattleFood = farmerCattleFood.reduce((sum, e) => sum + e.totalAmount, 0);
                    
                    // Calculate remaining bill
                    const remainingBill = totalMilkAmount - totalCattleFood;
                    
                    return (
                      <div key={`${farmer.id}-${index}`} className="farmer-card-premium farmer-slide-card">
                        <div className="card-shine"></div>
                        <div className="farmer-avatar-box" style={{ background: colorData.gradient }}>
                          <span className="avatar-emoji">{farmer.name.charAt(0).toUpperCase()}</span>
                          <div className="avatar-glow"></div>
                        </div>
                        <div className="farmer-info-premium">
                          <h3 className="farmer-name-premium">{farmer.name}</h3>
                          <span className="farmer-id-badge" style={{ background: colorData.light }}>
                            ID: {farmer.code}
                          </span>
                        </div>
                        <div className="farmer-meta-premium">
                          {farmer.village && (
                            <span className="meta-item">
                              <span className="meta-icon">📍</span>
                              {farmer.village}
                            </span>
                          )}
                        </div>
                        <div className="farmer-stats-premium">
                          <div className="stat-item">
                            <span className="stat-label">🥛 Milk</span>
                            <span className="stat-value">{totalMilk}L</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">💰 Bill</span>
                            <span className="stat-value">₹{totalMilkAmount.toFixed(0)}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">📊 Remaining</span>
                            <span className="stat-value" style={{ color: remainingBill > 0 ? '#10b981' : '#ef4444' }}>₹{remainingBill.toFixed(0)}</span>
                          </div>
                        </div>
                        <div className="farmer-status">
                          <span className="status-dot"></span>
                          Active Member
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="farmers-cta-premium">
                <div className="cta-box farmers-cta-box">
                  <div className="cta-icons">
                    <span>👨‍🌾</span><span>🤝</span><span>🥛</span>
                  </div>
                  <div className="cta-info">
                    <h3>Join {registeredFarmers.length}+ Farmers in Our Network</h3>
                    <p>Register today and start managing your dairy business efficiently</p>
                  </div>
                  <button className="cta-btn-premium" onClick={handleLoginClick}>
                    Register Now <span>→</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="farmers-empty-premium">
              <div className="empty-icon-box">👨‍🌾</div>
              <h3>No Farmers Registered Yet</h3>
              <p>Be the first to join our growing community</p>
              <button className="cta-btn-premium" onClick={handleLoginClick}>
                Be the First <span>→</span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Food Items Section - Premium Design */}
      <section className="food-section-premium">
        <div className="food-bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        
        <div className="section-container">
          <div className="food-header-premium">
            <div className="food-badge-wrapper">
              <span className="food-badge-premium">
                <span className="badge-dot"></span>
                Cattle Feed Store
              </span>
            </div>
            <h2 className="food-title-premium">Available Cattle Food Items</h2>
          </div>

          {loadingFood ? (
            <div className="food-loading-premium">
              <div className="loader-container">
                <div className="loader-ring"></div>
                <span className="loader-emoji">🌾</span>
              </div>
              <p>Loading food items...</p>
            </div>
          ) : foodItems.length > 0 ? (
            <>
              <div className="marquee-container">
                <div className="marquee-track marquee-track-reverse">
                  {[...foodItems, ...foodItems].map((item, index) => {
                    const foodData = [
                      { icon: '🌾', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)', light: '#d1fae5', name: 'Emerald' },
                      { icon: '🌿', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', light: '#fef3c7', name: 'Amber' },
                      { icon: '🥬', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', light: '#ede9fe', name: 'Violet' },
                      { icon: '🌽', color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', light: '#fee2e2', name: 'Red' },
                      { icon: '🥕', color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', light: '#cffafe', name: 'Cyan' },
                      { icon: '🍃', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899, #db2777)', light: '#fce7f3', name: 'Pink' },
                      { icon: '🌱', color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)', light: '#ccfbf1', name: 'Teal' },
                      { icon: '🥜', color: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)', light: '#ffedd5', name: 'Orange' }
                    ];
                    const data = foodData[index % foodData.length];
                    
                    return (
                      <div key={`${item.id}-${index}`} className="food-card-premium food-slide-card" style={{ '--accent-color': data.color, '--accent-gradient': data.gradient, '--accent-light': data.light }}>
                        <div className="card-shine"></div>
                        <div className="card-icon-box" style={{ background: data.gradient }}>
                          <span className="card-icon">{data.icon}</span>
                          <div className="icon-glow"></div>
                        </div>
                        <div className="card-details">
                          <h3 className="card-name">{item.name}</h3>
                          <div className="card-price">
                            <span className="price-currency">₹</span>
                            <span className="price-amount">{item.price}</span>
                            <span className="price-per">/{item.unit}</span>
                          </div>
                        </div>
                        <div className="card-meta">
                          <span className="meta-unit" style={{ background: data.light, color: data.color }}>
                            📦 {item.unit}
                          </span>
                          <span className="meta-stock">
                            <span className="stock-indicator" style={{ background: data.color }}></span>
                            Available
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="food-cta-premium">
                <div className="cta-box">
                  <div className="cta-icons">
                    <span>🐄</span><span>🌾</span><span>🥛</span>
                  </div>
                  <div className="cta-info">
                    <h3>Ready to Manage Your Cattle Feed?</h3>
                    <p>Track inventory, monitor costs, and optimize nutrition</p>
                  </div>
                  <button className="cta-btn-premium" onClick={handleLoginClick}>
                    Manage Feed <span>→</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="food-empty-premium">
              <div className="empty-icon-box">🌾</div>
              <h3>No Food Items Available</h3>
              <p>Start adding cattle feed items to your inventory</p>
              <button className="cta-btn-premium" onClick={handleLoginClick}>
                Add Items <span>+</span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Milk Prices Section */}
      

      {/* Enhanced Footer */}
      <footer className="footer-full">
        <div className="footer-full-top">
          <div className="footer-full-container">
            {/* Brand Section */}
            <div className="footer-full-brand">
              <div className="footer-full-logo">
                <span className="logo-icon">🐄</span>
                <div>
                  <h3>Dairy ERP System</h3>
                  <span className="tagline">Smart Dairy Management</span>
                </div>
              </div>
              <p className="footer-full-desc">
                A complete dairy management solution for modern farms. Streamline your milk collection, 
                farmer management, billing, and analytics all in one place.
              </p>
              <div className="footer-full-social">
                <a href="https://wa.me/917676831143" target="_blank" rel="noopener noreferrer" title="WhatsApp" className="social-btn whatsapp">
                  <FaWhatsapp />
                </a>
                <a href="mailto:akxayb1143@gmail.com" title="Email" className="social-btn email">
                  <FaEnvelope />
                </a>
                <a href="https://github.com/akshaybellagi" target="_blank" rel="noopener noreferrer" title="GitHub" className="social-btn github">
                  <FaGithub />
                </a>
                <a href="https://linkedin.com/in/akshaybellagi" target="_blank" rel="noopener noreferrer" title="LinkedIn" className="social-btn linkedin">
                  <FaLinkedin />
                </a>
                <a href="https://instagram.com/akshaybellagi" target="_blank" rel="noopener noreferrer" title="Instagram" className="social-btn instagram">
                  <FaInstagram />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="footer-full-links">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#features" onClick={(e) => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); }}>Features</a></li>
                <li><button onClick={handleLoginClick} className="footer-link-btn">Login / Demo</button></li>
                <li><a href="#farmers" onClick={(e) => { e.preventDefault(); document.querySelector('.farmers-section-premium')?.scrollIntoView({ behavior: 'smooth' }); }}>Our Farmers</a></li>
                <li><a href="#food" onClick={(e) => { e.preventDefault(); document.querySelector('.food-section-premium')?.scrollIntoView({ behavior: 'smooth' }); }}>Cattle Feed</a></li>
              </ul>
            </div>

            {/* Services */}
            <div className="footer-full-links">
              <h4>Services</h4>
              <ul>
                <li><button onClick={handleLoginClick} className="footer-link-btn">🥛 Milk Tracking</button></li>
                <li><button onClick={handleLoginClick} className="footer-link-btn">👨‍🌾 Farmer Management</button></li>
                <li><button onClick={handleLoginClick} className="footer-link-btn">💳 Billing System</button></li>
                <li><button onClick={handleLoginClick} className="footer-link-btn">📊 Reports & Analytics</button></li>
              </ul>
            </div>

            {/* Contact Section */}
            <div className="footer-full-contact">
              <h4>Contact Us</h4>
              <div className="contact-list">
                <a href="mailto:akxayb1143@gmail.com" className="contact-row">
                  <FaEnvelope className="contact-icon" />
                  <span>akxayb1143@gmail.com</span>
                </a>
                <a href="tel:+917676831143" className="contact-row">
                  <FaPhone className="contact-icon" />
                  <span>+91 7676831143</span>
                </a>
                <a href="https://wa.me/917676831143" target="_blank" rel="noopener noreferrer" className="contact-row">
                  <FaWhatsapp className="contact-icon" />
                  <span>WhatsApp: 7676831143</span>
                </a>
                <div className="contact-row">
                  <FaMapMarkerAlt className="contact-icon" />
                  <span>Kakamari, Athani, Belagavi,<br />Karnataka, India</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-full-bottom">
          <div className="footer-full-container">
            <p>© 2025 Dairy ERP System. All rights reserved.</p>
            <p className="developer-credit">
              Developed with <FaHeart className="heart-icon" /> by <a href="https://github.com/akshaybellagi" target="_blank" rel="noopener noreferrer" className="developer-link">Akshay Bellagi</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
